'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import {
  ArrowDown01Icon,
  Award01Icon,
  Coins01Icon,
  InformationCircleIcon,
} from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { cn } from '@/lib/ui/utils';

// ── Types ────────────────────────────────────────────────────────────────────

interface FeeLineItem {
  label: string;
  value: string;
  isNegative?: boolean;
}

/**
 * Which fee surface the breakdown belongs to.
 * - `trade`              — markets buy/sell/swap (default, existing behaviour).
 * - `winner`             — graduated winner / AMM token: only the cp-swap pool swap fee (no prize-pool or conviction fee).
 * - `proposal-fund`      — backing a proposal on the launchpad (backing fee).
 * - `proposal-withdraw`  — unbacking a proposal (withdrawal fee + conviction penalty disclaimer).
 */
type FeeKind = 'trade' | 'winner' | 'proposal-fund' | 'proposal-withdraw';

interface TradeDetailsProps {
  items: FeeLineItem[];
  onExpandedChange?: (expanded: boolean) => void;
  totalLabel: string;
  totalValue: string;
  /** Optional informational note displayed below fees (e.g. swap explanation). */
  infoNote?: string;
  /** When true, fee breakdown is not ready yet — header stays visible; expand is disabled. */
  feesRowHidden?: boolean;
  /** Initial quote work shows neutral placeholders instead of zero-valued output. */
  loading?: boolean;
  /** Selects the "Understanding fees" dialog body + per-surface chrome. Defaults to `trade`. */
  feeKind?: FeeKind;
}

// ── Understanding fees (dialog body only — opened via "Learn more" in expanded panel) ─

function UnderstandingFeesDialogBody(): React.ReactNode {
  const t = useTranslations('trade');

  return (
    <div className="flex flex-col gap-4 text-body">
      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-card bg-background-tertiary">
          <Icon icon={Coins01Icon} className="size-4 text-foreground-secondary" />
        </div>
        <div>
          <h4 className="font-medium text-foreground-primary">{t('fee_info.trade_title')}</h4>
          <p className="mt-0.5 text-foreground-secondary">{t('fee_info.trade_description')}</p>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-card bg-background-tertiary">
          <Icon icon={Award01Icon} className="size-4 text-foreground-secondary" />
        </div>
        <div>
          <h4 className="font-medium text-foreground-primary">{t('fee_info.prize_pool_title')}</h4>
          <p className="mt-0.5 text-foreground-secondary">{t('fee_info.prize_pool_description')}</p>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-card bg-background-tertiary">
          <Icon icon={InformationCircleIcon} className="size-4 text-foreground-secondary" />
        </div>
        <div>
          <h4 className="font-medium text-foreground-primary">{t('fee_info.penalty_title')}</h4>
          <p className="mt-0.5 text-foreground-secondary">{t('fee_info.penalty_description')}</p>
        </div>
      </div>

      <div className="rounded-overlay-item border border-border-primary/50 bg-background-tertiary/30 p-3 text-caption text-foreground-secondary">
        {t('fees.footer_note')}
      </div>
    </div>
  );
}

function WinnerUnderstandingFeesDialogBody(): React.ReactNode {
  const t = useTranslations('trade');

  return (
    <div className="flex flex-col gap-4 text-body">
      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-card bg-background-tertiary">
          <Icon icon={Coins01Icon} className="size-4 text-foreground-secondary" />
        </div>
        <div>
          <h4 className="font-medium text-foreground-primary">{t('fee_info.winner_title')}</h4>
          <p className="mt-0.5 text-foreground-secondary">{t('fee_info.winner_description')}</p>
        </div>
      </div>

      <div className="rounded-overlay-item border border-border-primary/50 bg-background-tertiary/30 p-3 text-caption text-foreground-secondary">
        {t('fees.footer_note')}
      </div>
    </div>
  );
}

function ProposalUnderstandingFeesDialogBody(): React.ReactNode {
  const tProposalFees = useTranslations('launchpad.fees');

  return (
    <div className="flex flex-col gap-4 text-body">
      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-card bg-background-tertiary">
          <Icon icon={Coins01Icon} className="size-4 text-foreground-secondary" />
        </div>
        <div>
          <h4 className="font-medium text-foreground-primary">
            {tProposalFees('backingFeeTitle')}
          </h4>
          <p className="mt-0.5 text-foreground-secondary">
            {tProposalFees('backingFeeDescription')}
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-card bg-background-tertiary">
          <Icon icon={InformationCircleIcon} className="size-4 text-foreground-secondary" />
        </div>
        <div>
          <h4 className="font-medium text-foreground-primary">
            {tProposalFees('withdrawFeeTitle')}
          </h4>
          <p className="mt-0.5 text-foreground-secondary">
            {tProposalFees('withdrawFeeDescription')}
          </p>
        </div>
      </div>

      <div className="rounded-overlay-item border border-border-primary/50 bg-background-tertiary/30 p-3 text-caption text-foreground-secondary">
        {tProposalFees('footerNote')}
      </div>
    </div>
  );
}

// ── Trade Details (Collapsible) ──────────────────────────────────────────────

export function TradeDetails({
  items,
  onExpandedChange,
  totalLabel,
  totalValue,
  infoNote,
  feesRowHidden = false,
  loading = false,
  feeKind = 'trade',
}: TradeDetailsProps): React.ReactNode {
  const t = useTranslations('trade');
  const tLaunchpadFees = useTranslations('launchpad.fees');
  const [expanded, setExpanded] = useState(false);
  const [understandingFeesOpen, setUnderstandingFeesOpen] = useState(false);

  const isProposal = feeKind === 'proposal-fund' || feeKind === 'proposal-withdraw';
  const isProposalWithdraw = feeKind === 'proposal-withdraw';
  const dialogTitle = isProposal ? tLaunchpadFees('understandingFeesTitle') : t('fee_info.title');

  useEffect(() => {
    if (feesRowHidden) setExpanded(false);
  }, [feesRowHidden]);

  function toggleExpanded(): void {
    if (feesRowHidden) return;
    onExpandedChange?.(!expanded);
    setExpanded(!expanded);
  }

  return (
    <div className="flex flex-col">
      {/* ── Toggle row: full-row expand/collapse hit target (Learn more lives in expanded panel) ─ */}
      <div
        className={cn(
          'relative flex w-full items-center justify-between rounded-panel-row px-2 py-[4px] text-caption text-foreground-tertiary transition-[color] duration-fast ease-default motion-reduce:transition-none',
          feesRowHidden ? 'text-foreground-tertiary/80' : 'hover:text-foreground-primary',
        )}
      >
        {!feesRowHidden ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? t('fees.collapse_label') : t('fees.expand_label')}
            className="absolute inset-0 z-0 rounded-panel-row outline-none focus-visible:ring-2 focus-visible:ring-border-primary/40"
            onClick={toggleExpanded}
          />
        ) : null}
        <div className="relative z-10 flex w-full min-w-0 items-center justify-between pointer-events-none">
          <span className="min-w-0 truncate">{t('fees.estimated_label')}</span>
          <Icon
            icon={ArrowDown01Icon}
            aria-hidden
            className={cn(
              'size-3 shrink-0 text-foreground-tertiary transition-transform duration-normal',
              expanded && !feesRowHidden && 'rotate-180',
              feesRowHidden && 'opacity-40',
            )}
          />
        </div>
      </div>

      {loading ? (
        <output className="flex flex-col gap-2 px-2 py-2" aria-label={t('status.loading_quote')}>
          <Skeleton className="h-4 w-full rounded" />
          <Skeleton className="h-4 w-4/5 rounded" />
        </output>
      ) : null}

      {/* ── Expandable content ──────────────────────────────────────────── */}
      {expanded && !feesRowHidden && (
        <div className="flex flex-col gap-2 rounded-panel-row border border-border-primary/40 bg-background-tertiary/20 px-3 py-3 text-caption animate-in fade-in-0 slide-in-from-top-1 duration-fast">
          {/* Fee line items */}
          {items.map((item) => (
            <div key={item.label} className="flex justify-between gap-3">
              <span className="text-foreground-secondary">{item.label}</span>
              <span className="text-right font-medium tabular-nums text-foreground-primary">
                {item.isNegative === true ? '-' : ''}
                {item.value}
              </span>
            </div>
          ))}

          {/* Total */}
          <div className="flex justify-between gap-3 border-t border-border-primary/40 pt-2 text-foreground-primary">
            <span className="font-medium">{totalLabel}</span>
            <span className="font-semibold tabular-nums">{totalValue}</span>
          </div>

          {infoNote !== undefined && infoNote.length > 0 && (
            <div className="mt-1 flex items-start gap-1.5 rounded bg-background-tertiary/40 px-2 py-1.5 text-caption text-foreground-secondary">
              <Icon icon={InformationCircleIcon} className="mt-0.5 size-3 shrink-0" />
              <span>{infoNote}</span>
            </div>
          )}

          {isProposalWithdraw && (
            <p className="mt-1 text-caption leading-relaxed text-foreground-tertiary">
              {tLaunchpadFees('withdrawDisclaimer')}
            </p>
          )}

          <div className="mt-1 flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-caption text-foreground-secondary">
              <span className="size-2 rounded-full bg-foreground-tertiary" aria-hidden="true" />
              {t('fees.calculated_from_pool')}
            </div>
            <AnimatedButton
              type="button"
              variant="tertiary"
              size="xs"
              className="ml-auto px-0 text-caption underline underline-offset-4 hover:bg-transparent"
              aria-haspopup="dialog"
              aria-expanded={understandingFeesOpen}
              disableHoverScale
              onClick={() => setUnderstandingFeesOpen(true)}
            >
              {t('fees.learn_more')}
            </AnimatedButton>
          </div>
        </div>
      )}

      <SilkSheet
        open={understandingFeesOpen}
        onOpenChange={setUnderstandingFeesOpen}
        variant="detached"
        size="md"
        title={dialogTitle}
      >
        {feeKind === 'winner' ? (
          <WinnerUnderstandingFeesDialogBody />
        ) : isProposal ? (
          <ProposalUnderstandingFeesDialogBody />
        ) : (
          <UnderstandingFeesDialogBody />
        )}
      </SilkSheet>
    </div>
  );
}
